<script setup>
import { ref, computed, watch, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import callDevicesAPI from 'dashboard/api/callDevices';

const emit = defineEmits(['close', 'deviceAdded']);

const { t } = useI18n();

// — step: 'name' | 'qr' | 'connected'
const step = ref('name');
// — mode: 'add' (new device, asks for a name first) | 'repair' (existing device, straight to QR)
const mode = ref('add');
const deviceName = ref('');
const isCreating = ref(false);
const errorMessage = ref('');
const currentQr = ref('');
const deviceId = ref(null);
const dialogRef = ref(null);
const canvasRef = ref(null);
let pollTimer = null;

const dialogTitle = computed(() =>
  mode.value === 'repair'
    ? t('CALL_DEVICES.ADD_MODAL.REPAIR_TITLE')
    : t('CALL_DEVICES.ADD_MODAL.TITLE')
);

// ── helpers ──────────────────────────────────────────────────────────────────

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const renderQr = async qrString => {
  if (!qrString || !canvasRef.value) return;
  try {
    await QRCode.toCanvas(canvasRef.value, qrString, {
      width: 240,
      margin: 2,
      color: { dark: '#000000', light: '#ffffff' },
    });
  } catch {
    // ignore render errors — next poll will retry
  }
};

const pollDevice = () => {
  pollTimer = setInterval(async () => {
    if (!deviceId.value) return;
    try {
      const { data } = await callDevicesAPI.show(deviceId.value);
      if (data.paired) {
        stopPolling();
        step.value = 'connected';
        setTimeout(() => {
          dialogRef.value?.close();
          emit('deviceAdded');
        }, 1800);
        return;
      }
      if (data.qr && data.qr !== currentQr.value) {
        currentQr.value = data.qr;
        await nextTick();
        renderQr(data.qr);
      }
    } catch {
      // network hiccup — keep polling
    }
  }, 2000);
};

// ── public open/close ────────────────────────────────────────────────────────

const open = () => {
  mode.value = 'add';
  step.value = 'name';
  deviceName.value = '';
  isCreating.value = false;
  errorMessage.value = '';
  currentQr.value = '';
  deviceId.value = null;
  dialogRef.value?.open();
};

// Open straight into the QR step to re-pair an existing device (reconnect).
// `initialQr` is the QR returned by the pair endpoint, shown immediately while
// polling picks up rotations and the eventual paired=true.
const openForDevice = async (id, initialQr = '') => {
  mode.value = 'repair';
  step.value = 'qr';
  deviceName.value = '';
  isCreating.value = false;
  errorMessage.value = '';
  currentQr.value = initialQr;
  deviceId.value = id;
  dialogRef.value?.open();
  if (initialQr) {
    await nextTick();
    renderQr(initialQr);
  }
  pollDevice();
};

const handleClose = () => {
  stopPolling();
  emit('close');
};

const handleCreate = async () => {
  isCreating.value = true;
  errorMessage.value = '';
  try {
    const { data } = await callDevicesAPI.create({
      name: deviceName.value.trim() || 'WhatsApp',
    });
    deviceId.value = data.id;
    step.value = 'qr';
    // Start polling — first render will happen once canvasRef is in DOM
    pollDevice();
  } catch (error) {
    // 422 device_limit_reached vinha mudo (só console) — mostra o motivo real.
    const payload = error?.response?.data;
    if (payload?.error === 'device_limit_reached') {
      // limite 0 = a conta ainda não tem NENHUM dispositivo (sem caixa WAHA
      // nem CallingPlan de cortesia) — "Limite atingido (0)" confunde quem
      // acabou de pagar o módulo; a mensagem certa é "conecte uma caixa WAHA".
      errorMessage.value =
        payload.limit === 0
          ? t('CALL_DEVICES.TOAST.LIMIT_REACHED_ZERO')
          : t('CALL_DEVICES.TOAST.LIMIT_REACHED', { limit: payload.limit });
    } else {
      errorMessage.value = t('CALL_DEVICES.TOAST.CREATE_ERROR');
    }
  } finally {
    isCreating.value = false;
  }
};

// Watch the canvas being mounted (step changes to 'qr') then render immediately
watch(
  () => canvasRef.value,
  async canvas => {
    if (canvas && currentQr.value) {
      await renderQr(currentQr.value);
    }
  }
);

onUnmounted(() => {
  stopPolling();
});

defineExpose({ open, openForDevice });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="dialogTitle"
    :show-confirm-button="false"
    :show-cancel-button="false"
    width="md"
    @close="handleClose"
  >
    <!-- Step: name entry -->
    <template v-if="step === 'name'">
      <Input
        v-model="deviceName"
        :label="t('CALL_DEVICES.ADD_MODAL.NAME_LABEL')"
        :placeholder="t('CALL_DEVICES.ADD_MODAL.NAME_PLACEHOLDER')"
        @enter="handleCreate"
      />
      <p v-if="errorMessage" class="text-sm text-n-ruby-9 mt-1">
        {{ errorMessage }}
      </p>
      <div class="flex items-center justify-end gap-3 mt-2">
        <Button
          variant="faded"
          color="slate"
          :label="t('CALL_DEVICES.ADD_MODAL.CANCEL')"
          @click="dialogRef?.close()"
        />
        <Button
          :label="t('CALL_DEVICES.ADD_MODAL.CREATE')"
          :is-loading="isCreating"
          @click="handleCreate"
        />
      </div>
    </template>

    <!-- Step: QR scanning -->
    <template v-else-if="step === 'qr'">
      <div class="flex flex-col items-center gap-4 py-2">
        <div
          v-if="!currentQr"
          class="flex items-center justify-center w-60 h-60 rounded-xl bg-n-alpha-1"
        >
          <div class="flex flex-col items-center gap-2 text-n-slate-10">
            <span class="i-lucide-loader-2 animate-spin size-8" />
            <span class="text-sm">{{
              t('CALL_DEVICES.ADD_MODAL.WAITING_QR')
            }}</span>
          </div>
        </div>
        <canvas
          v-show="currentQr"
          ref="canvasRef"
          class="rounded-xl overflow-hidden"
        />
        <div class="text-center space-y-1">
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('CALL_DEVICES.ADD_MODAL.SCANNING') }}
          </p>
          <p class="text-xs text-n-slate-10">
            {{ t('CALL_DEVICES.ADD_MODAL.QR_ROTATE') }}
          </p>
        </div>
      </div>
    </template>

    <!-- Step: connected -->
    <template v-else-if="step === 'connected'">
      <div class="flex flex-col items-center gap-4 py-6">
        <span class="i-lucide-circle-check-big size-14 text-n-teal-9" />
        <p class="text-base font-medium text-n-slate-12 text-center">
          {{ t('CALL_DEVICES.ADD_MODAL.CONNECTED') }}
        </p>
      </div>
    </template>
  </Dialog>
</template>
